import {
  collection,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore'
import {
  getGlobalCollection,
  getInstituteCollection,
  resolveScopedCollection,
  resolveScopedDocRef,
} from '@/services/firestorePaths'
import { db } from '@/services/firebase'
import { omitUndefined } from '@/utils/objectUtils'
import type { Test, TestCreatePayload, TestUpdatePayload } from '@/types'

/**
 * @auto-generated — DESCRIBE: What values this type allows and in which contexts it is used.
 */
export type TestWithScope = Test & { isGlobal: boolean }

interface ServiceError {
  code: string
  message: string
  originalError: unknown
}

function createServiceError(originalError: unknown): ServiceError {
  const err = originalError as { code?: string; message?: string }
  return {
    code: err.code ?? 'unknown',
    message: err.message ?? 'An unexpected error occurred',
    originalError,
  }
}

/**
 * Fetch a single Test document by ID and scope.
 * If scope is not provided, searches global first, then all institutes (slower).
 */
export async function getTest(id: string, scope?: string | 'global'): Promise<TestWithScope | null> {
  try {
    if (scope) {
      const ref = resolveScopedDocRef('tests', id, scope)
      const snap = await getDoc(ref)
      if (!snap.exists()) return null
      return {
        id: snap.id,
        ...snap.data(),
        isGlobal: scope === 'global'
      } as TestWithScope
    }
    
    // Try global first
    const globalRef = resolveScopedDocRef('tests', id, 'global')
    const globalSnap = await getDoc(globalRef)
    if (globalSnap.exists()) {
      return {
        id: globalSnap.id,
        ...globalSnap.data(),
        isGlobal: true
      } as TestWithScope
    }
    
    // Fallback: search all institutes (expensive, prefer passing scope)
    const institutesSnap = await getDocs(collection(db, 'institutes'))
    for (const instDoc of institutesSnap.docs) {
      const instRef = resolveScopedDocRef('tests', id, instDoc.id)
      const instSnap = await getDoc(instRef)
      if (instSnap.exists()) {
        return {
          id: instSnap.id,
          ...instSnap.data(),
          isGlobal: false
        } as TestWithScope
      }
    }
    
    return null
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Fetch multiple tests by their IDs (batch fetch with Promise.all).
 * Returns only tests that exist; null results are filtered out.
 */
export async function getTests(ids: string[], scope?: string | 'global'): Promise<TestWithScope[]> {
  if (!ids.length) return []
  const results = await Promise.all(ids.map((id) => getTest(id, scope)))
  return results.filter(Boolean) as TestWithScope[]
}

/**
 * List tests by institute with global tests included (parallel query pattern).
 * This is the recommended function for optimal performance.
 */
export async function listTestsByInstituteId(instituteId: string): Promise<TestWithScope[]> {
  try {
    const [globalSnap, instituteSnap] = await Promise.all([
      getDocs(getGlobalCollection('tests')),
      getDocs(getInstituteCollection('tests', instituteId))
    ])
    
    const globalTests = globalSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      isGlobal: true
    } as TestWithScope))
    
    const instituteTests = instituteSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      isGlobal: false
    } as TestWithScope))
    
    return [...globalTests, ...instituteTests]
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Fetch tests belonging to a specific unit.
 */
export async function listTestsByUnitId(unitId: string, instituteId: string): Promise<TestWithScope[]> {
  try {
    const allTests = await listTestsByInstituteId(instituteId)
    return allTests.filter((test) => test.unitId === unitId)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Fetch all tests across all institutes (use with caution - expensive).
 */
export async function listAllTests(): Promise<TestWithScope[]> {
  try {
    const institutesSnap = await getDocs(collection(db, 'institutes'))
    const [globalSnap, ...instituteSnaps] = await Promise.all([
      getDocs(getGlobalCollection('tests')),
      ...institutesSnap.docs.map((instDoc) =>
        getDocs(getInstituteCollection('tests', instDoc.id))
      )
    ])
    
    const globalTests = globalSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      isGlobal: true
    } as TestWithScope))
    
    const instituteTests = instituteSnaps.flatMap((snap) =>
      snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        isGlobal: false
      } as TestWithScope))
    )
    
    return [...globalTests, ...instituteTests]
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * List tests by multiple institutes (includes global).
 */
export async function listTestsByInstitutes(institutes: string[]): Promise<TestWithScope[]> {
  if (institutes.length === 0) return []
  try {
    const [globalSnap, ...instituteSnaps] = await Promise.all([
      getDocs(getGlobalCollection('tests')),
      ...institutes.map((instituteId) => getDocs(getInstituteCollection('tests', instituteId)))
    ])
    
    const globalTests = globalSnap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      isGlobal: true
    } as TestWithScope))
    
    const instituteTests = instituteSnaps.flatMap((snap) =>
      snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        isGlobal: false
      } as TestWithScope))
    )
    
    return [...globalTests, ...instituteTests]
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Create a new test in the specified scope.
 * @param payload - Test data
 * @param scope - 'global' (superuser only) or institute ID
 */
export async function createTest(
  payload: Omit<TestCreatePayload, 'ownedBy'>,
  scope: string | 'global'
): Promise<string> {
  if (!scope) {
    throw new Error('[test.service] scope is required')
  }
  try {
    // Remove ownedBy if present
    const { ownedBy: _, ...cleanPayload } = payload as any
    
    const ref = await addDoc(resolveScopedCollection('tests', scope), {
      ...omitUndefined(cleanPayload),
      instituteId: scope === 'global' ? undefined : scope,
      createdAt: serverTimestamp(),
    })
    return ref.id
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Update a test in the specified scope.
 * @param id - Test ID
 * @param scope - 'global' or institute ID
 * @param payload - Fields to update
 */
export async function updateTest(
  id: string,
  scope: string | 'global',
  payload: Omit<TestUpdatePayload, 'ownedBy'>
): Promise<void> {
  try {
    // Remove ownedBy if present
    const { ownedBy: _, ...cleanPayload } = payload as any
    
    const df = deleteField()
    await updateDoc(resolveScopedDocRef('tests', id, scope), {
      ...cleanPayload,
      ...('isNavigable' in cleanPayload && cleanPayload.isNavigable === undefined ? { isNavigable: df } : {}),
      ...('retryPolicy' in cleanPayload && cleanPayload.retryPolicy === undefined ? { retryPolicy: df } : {}),
      ...('testRetryCount' in cleanPayload && cleanPayload.testRetryCount === undefined ? { testRetryCount: df } : {}),
      ...('tags' in cleanPayload && cleanPayload.tags === undefined ? { tags: df } : {}),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Delete a test from the specified scope.
 * @param id - Test ID
 * @param scope - 'global' or institute ID
 */
export async function deleteTest(id: string, scope: string | 'global'): Promise<void> {
  try {
    await deleteDoc(resolveScopedDocRef('tests', id, scope))
  } catch (error) {
    throw createServiceError(error)
  }
}
